import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL } from "../lib/format";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { Printer, RefreshCw } from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const Blok = ({ baslik, children }) => (
  <div className="rounded-lg border p-3 break-inside-avoid">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">{baslik}</p>
    {children}
  </div>
);

const Satir = ({ ad, deger, kalin, kirmizi }) => (
  <p className={`flex justify-between text-sm tabular-nums ${kalin ? "font-bold border-t border-border/60 pt-1 mt-1" : ""}`}>
    <span className={kalin ? "" : "text-muted-foreground"}>{ad}</span>
    <span className={kirmizi ? "text-red-700 font-semibold" : "font-semibold"}>{deger}</span>
  </p>
);

export const AylikOzetRaporu = ({ yil = 2026 }) => {
  const [ay, setAy] = useState(String(new Date().getMonth() + 1));
  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/rapor/aylik-ozet", { params: { yil, ay: parseInt(ay) } });
      setRapor(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Aylık özet yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [yil, ay]);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  return (
    <div className="space-y-3" data-testid="aylik-ozet-raporu">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #aylik-ozet-yazdir, #aylik-ozet-yazdir * { visibility: visible !important; }
          #aylik-ozet-yazdir { position: absolute; inset: 0; padding: 24px; background: white; }
        }
      `}</style>
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Select value={ay} onValueChange={setAy}>
          <SelectTrigger className="h-10 w-40" data-testid="aylik-ozet-ay-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {AYLAR.map((a, i) => <SelectItem key={a} value={String(i + 1)}>{a} {yil}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchRapor} className="h-10 gap-1.5" data-testid="aylik-ozet-yenile-button">
          <RefreshCw className="h-4 w-4" /> Yenile
        </Button>
        <Button onClick={() => window.print()} className="h-10 gap-1.5 ml-auto" data-testid="aylik-ozet-yazdir-button">
          <Printer className="h-4 w-4" /> Yazdır / PDF Kaydet
        </Button>
      </div>

      {loading && !rapor ? (
        <Skeleton className="h-72" />
      ) : !rapor ? null : (
        <div id="aylik-ozet-yazdir" className="rounded-xl border bg-card p-5 space-y-4">
          <div className="flex items-baseline justify-between border-b pb-3">
            <div>
              <h2 className="font-display text-lg font-bold">3A Otomotiv — Aylık Finansal Özet</h2>
              <p className="text-sm text-muted-foreground">{AYLAR[rapor.ay - 1]} {rapor.yil}</p>
            </div>
            <p className="text-xs text-muted-foreground">Rapor tarihi: {new Date().toLocaleDateString("tr-TR")}</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-[10px] font-semibold uppercase text-muted-foreground">Ciro (Matrah)</p>
              <p className="font-display text-lg font-bold tabular-nums" data-testid="ozet-ciro">{formatTL(rapor.ciro.matrah)}</p>
              <p className="text-[10px] text-muted-foreground">{rapor.ciro.adet} fatura · KDV dahil {formatTL(rapor.ciro.toplam)}</p>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-[10px] font-semibold uppercase text-muted-foreground">Tahsilat</p>
              <p className="font-display text-lg font-bold tabular-nums text-emerald-700" data-testid="ozet-tahsilat">{formatTL(rapor.tahsilat.toplam)}</p>
              <p className="text-[10px] text-muted-foreground">{rapor.tahsilat.adet} işlem</p>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-[10px] font-semibold uppercase text-muted-foreground">Giderler</p>
              <p className="font-display text-lg font-bold tabular-nums text-red-700" data-testid="ozet-gider">{formatTL(rapor.giderler.toplam)}</p>
              <p className="text-[10px] text-muted-foreground">+ Ödemeler {formatTL(rapor.odemeler?.toplam || 0)}</p>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-[10px] font-semibold uppercase text-muted-foreground">Net Kâr</p>
              <p className={`font-display text-lg font-bold tabular-nums ${rapor.net_kar < 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="ozet-net-kar">{formatTL(rapor.net_kar)}</p>
              <p className="text-[10px] text-muted-foreground">Brüt kâr {formatTL(rapor.brut_kar)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Blok baslik="Tahsilatlar — Nereye Yapıldı">
              {rapor.tahsilat.yerler.length === 0 ? (
                <p className="text-sm text-muted-foreground">Bu ay tahsilat yok.</p>
              ) : (
                rapor.tahsilat.yerler.map((y) => (
                  <Satir key={y.odeme_yeri} ad={`${y.odeme_yeri} (${y.adet})`} deger={formatTL(y.toplam)} />
                ))
              )}
              <Satir ad="Toplam Tahsilat" deger={formatTL(rapor.tahsilat.toplam)} kalin />
            </Blok>
            <Blok baslik="Giderler — Grup Bazında">
              {rapor.giderler.gruplar.length === 0 ? (
                <p className="text-sm text-muted-foreground">Bu ay gider kaydı yok.</p>
              ) : (
                rapor.giderler.gruplar.map((g) => <Satir key={g.grup} ad={g.grup} deger={formatTL(g.toplam)} />)
              )}
              <Satir ad="Toplam Gider" deger={formatTL(rapor.giderler.toplam)} kalin kirmizi />
            </Blok>
          </div>

          <Blok baslik="Özet Durum">
            <Satir ad="Ciro (Matrah)" deger={formatTL(rapor.ciro.matrah)} />
            <Satir ad="Dış Kaynak Giderleri" deger={`− ${formatTL(rapor.dis_kaynak.toplam)}`} />
            <Satir ad="Brüt Kâr" deger={formatTL(rapor.brut_kar)} kalin />
            <Satir ad="İşletme Giderleri" deger={`− ${formatTL(rapor.giderler.toplam)}`} />
            <Satir ad="Net Kâr" deger={formatTL(rapor.net_kar)} kalin kirmizi={rapor.net_kar < 0} />
            <div className="mt-2 pt-2 border-t border-dashed">
              <Satir ad="Nakit Bazlı Net (Tahsilat − Gider − Ödemeler)" deger={formatTL(rapor.nakit_net)} kalin kirmizi={rapor.nakit_net < 0} />
            </div>
          </Blok>
        </div>
      )}
    </div>
  );
};
